// apps/landing-page/src/lib/image-hosts.ts
import { isLiteralHostname } from './extra-image-host';

/**
 * Hosts covered by next.config.ts's images.remotePatterns. Keep this list in sync
 * with that config.
 *
 * Some image fields rendered on this app are free-typed admin inputs, not
 * media-picker-backed R2 uploads — e.g. LiveEvent.prizes[].imageUrl and
 * BlogPost.ogImageUrl have no server-side domain validation and could point
 * anywhere. next/image throws for a src whose host isn't in remotePatterns
 * (unlike a plain <img>, which just quietly fails to load), so those fields are
 * checked against this allowlist at render time and rendered `unoptimized` when
 * the host isn't confirmed, instead of risking a broken image on a public page.
 */
const KNOWN_IMAGE_HOSTS = [
  /\.r2\.cloudflarestorage\.com$/,
  /^media\.aksademia\.com$/,
  /^logo\.clearbit\.com$/,
];

/**
 * Build-time extra host (demo media bucket); mirrors the pattern next.config.ts appends. The build has
 * already failed on an invalid value (parseExtraImageHost there), so the literal-hostname check here only
 * guarantees that a value that is not a literal host can never count as known.
 */
function extraHostMatches(hostname: string): boolean {
  const extra = process.env.NEXT_PUBLIC_EXTRA_IMAGE_HOST;
  return extra !== undefined && isLiteralHostname(extra) && hostname === extra;
}

/** True for same-origin relative paths and any absolute URL on a known-safe host. */
export function isKnownImageHost(url: string | null | undefined): boolean {
  if (!url) return false;
  if (url.startsWith('/')) return true;
  try {
    const { hostname } = new URL(url);
    return KNOWN_IMAGE_HOSTS.some((pattern) => pattern.test(hostname)) || extraHostMatches(hostname);
  } catch {
    return false;
  }
}
